import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Modal } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { decode, encode } from 'html-entities';

import { useAppSelector } from 'hooks';
import { DateTimePicker, TextArea } from 'components/UI';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';

import RecurrenceEngine from '../../../RecurrenceEngine';

interface IAddEditMessageModalProps {
  show: boolean;
  handleClose: any;
  handleEdit: any;
  message?: ICarePlanMessage;
  carePlanTemplate?: ICarePlanTemplate;
}

const AddEditMessageModal = ({
  show,
  handleClose,
  handleEdit,
  message,
  carePlanTemplate
}: IAddEditMessageModalProps) => {
  const intl = useIntl();
  const patientUid = useAppSelector(({ patient }) => patient.data?.uid);
  const locale = useAppSelector((state) => state.intl.locale);
  const [notificationContent, setNotificationContent] = useState(
    message?.message || intl.formatMessage({ id: 'timeToTakeMeasurement' })
  );
  const [recurrence, setRecurrence] = useState(message?.isRepeating ? message.recurrence : '');
  const [validRecurrence, setValidRecurrence] = useState(true);
  const [editButtonDisabled, setEditButtonDisabled] = useState(true);

  const now = DateTime.now().setLocale(locale);
  const messageDate =
    message?.send_time && typeof message?.send_time !== 'number'
      ? DateTime.fromFormat(message.send_time, 'yyyy-MM-dd HH:mm:ss')
      : undefined;
  const [date, setDate] = useState<DateTime>(messageDate || now.plus({ hours: 1 }));

  const [{ loading, error: postError }, post] = useAxios(
    {
      url: carePlanTemplate
        ? '/clinician/2.0.0/CarePlanTemplate'
        : `/clinician/Patient/${patientUid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const _handleClose = () => {
    handleClose();
  };

  const _handleEdit = async () => {
    const messageToUpdate = {
      edit_status: message ? 'updated' : 'new',
      id: message?.id,
      action: {
        message: notificationContent,
        localDateTime: date.toFormat('yyyy-MM-dd HH:mm')
      },
      recurrence
    };

    await post({
      data: {
        scheduled: [messageToUpdate],
        carePlanTemplate
      }
    });

    handleEdit();
  };

  const onChangeRecurrence = (_recurrence: string, _valid: boolean) => {
    setRecurrence(_recurrence);
    setValidRecurrence(_valid);
  };

  useEffect(() => {
    const isMessageIdentical =
      notificationContent === message?.message &&
      recurrence === (message?.isRepeating ? message.recurrence : '') &&
      date.toMillis() === messageDate?.toMillis();
    setEditButtonDisabled(!notificationContent || !validRecurrence || isMessageIdentical);
  }, [notificationContent, validRecurrence, recurrence, date, messageDate, message]);

  return (
    <Modal show={show} onHide={_handleClose} backdrop="static" data-test="careplan-messages-modal">
      <Modal.Header className="align-items-center" closeButton>
        <Modal.Title>
          {intl.formatMessage({ id: message ? 'editMessageKey' : 'addMessageKey' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TextArea
          value={decode(notificationContent)}
          onChange={(e) => setNotificationContent(encode(e.target.value))}
          label={intl.formatMessage({ id: 'messageKey' })}
          rows={3}
        />

        <div className="d-flex flex-column mt-4">
          <DateTimePicker date={date} todayLimit="min" onChangeDate={setDate} minDate={now} />
        </div>

        <RecurrenceEngine
          recurrence={recurrence}
          isActive={message?.isRepeating || false}
          onChange={onChangeRecurrence}
          date={date}
        />

        {loading && <Loading className="mt-4" />}
        {postError && <Error className="mt-4" retry={_handleEdit} dismissible={false} />}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={_handleClose} data-test="careplan-messages-modal-close-btn">
          {intl.formatMessage({ id: 'closeKey' })}
        </Button>
        <Button
          variant="primary"
          onClick={_handleEdit}
          disabled={editButtonDisabled}
          data-test="careplan-messages-modal-add-btn"
        >
          {intl.formatMessage({ id: message ? 'saveButtonTitle' : 'add' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditMessageModal;
